if (!customElements.get('quantity-deal-promo')) {
  const trackedLostOpportunityKeys = new Set();

  class QuantityDealPromo extends HTMLElement {
    constructor() {
      super();
      this.qty = 1;
      this.basePriceCents = 0;
      this.compareAtPriceCents = 0;
      this.productId = '';
      this.inventoryQuantity = NaN;
      this.inventoryManagement = '';
      this.variantSku = '';
      this.threshold = 10;
      this.discountPercent = 20;
      this.bulkConfig = [];
      this.skuLookup = new Map();
      this.i18n = {};
      this.isActive = false;
      this.originalBtnText = '';
      this.variantChangeUnsub = undefined;
      this.quantityUpdateUnsub = undefined;
    }

    connectedCallback() {
      const bulkConfigNode = this.querySelector('.quantity-deal-promo__bulk-config');
      const i18nNode = this.querySelector('.quantity-deal-promo__i18n');

      try {
        this.bulkConfig = JSON.parse(bulkConfigNode?.textContent || '[]');
      } catch {
        this.bulkConfig = [];
      }

      this.buildSkuLookup();

      try {
        this.i18n = JSON.parse(i18nNode?.textContent || '{}');
      } catch {
        this.i18n = {};
      }

      this.sectionId = this.dataset.sectionId;
      this.form = document.getElementById(this.dataset.formId);
      this.threshold = parseInt(this.dataset.threshold, 10) || 10;
      this.discountPercent = parseFloat(this.dataset.discountPercent) || 20;

      this.titleEl = this.querySelector('.quantity-deal-promo__title');
      this.priceEl = this.querySelector('.quantity-deal-promo__price');
      this.compareEl = this.querySelector('.quantity-deal-promo__compare');
      this.saveRight = this.querySelector('.quantity-deal-promo__right');
      this.saveAmountEl = this.querySelector('.quantity-deal-promo__save-amount');
      this.saveTotalEl = this.querySelector('.quantity-deal-promo__save-total');
      this.dealBtn = this.querySelector('.quantity-deal-promo__deal-btn');

      this.variantSku = this.dataset.variantSku || '';
      this.basePriceCents = parseInt(this.dataset.variantPrice, 10) || 0;
      this.compareAtPriceCents = parseInt(this.dataset.variantCompareAtPrice, 10) || 0;
      this.syncVariantMeta({
        sku: this.variantSku,
        price: this.basePriceCents,
        compare_at_price: this.compareAtPriceCents,
        inventory_quantity: parseInt(this.dataset.inventoryQuantity, 10),
        inventory_management: this.dataset.inventoryManagement || '',
      });
      this.productId = this.dataset.productId || '';

      this.dealBtn?.addEventListener('click', () => this.addDealToCart());
      this.saveOriginalBtnText();

      this.variantChangeUnsub = FoxTheme.pubsub.subscribe(
        FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange,
        this.onVariantChange.bind(this)
      );

      this.quantityUpdateUnsub = FoxTheme.pubsub.subscribe(
        FoxTheme.pubsub.PUB_SUB_EVENTS.quantityUpdate,
        this.onQuantityUpdate.bind(this)
      );

      this.apply();
    }

    disconnectedCallback() {
      if (this.variantChangeUnsub) this.variantChangeUnsub();
      if (this.quantityUpdateUnsub) this.quantityUpdateUnsub();
    }

    onVariantChange({ data }) {
      if (!data || data.sectionId !== this.sectionId) return;
      const variant = data.variant;
      this.form = document.getElementById(this.dataset.formId);
      if (!variant) {
        this.deactivate();
        return;
      }
      this.syncVariantMeta(variant);
      this.apply();
    }

    onQuantityUpdate() {
      if (!this.isActive) return;
      const input = this.getQuantityInput();
      if (!input) return;
      const parsed = parseInt(input.value, 10);
      if (!parsed || parsed === this.qty) return;
      this.qty = parsed;
      this.updateUI();
    }

    buildSkuLookup() {
      this.skuLookup = new Map();

      for (const entry of this.bulkConfig) {
        const skus = [];

        if (Array.isArray(entry.skus)) {
          skus.push(...entry.skus);
        } else if (entry.sku) {
          skus.push(entry.sku);
        }

        for (const sku of skus) {
          const normalized = (sku || '').trim().toLowerCase();
          if (normalized) {
            this.skuLookup.set(normalized, entry);
          }
        }
      }
    }

    isInBulkList() {
      const sku = (this.variantSku || '').trim().toLowerCase();
      if (!sku) return false;
      return this.skuLookup.has(sku);
    }

    hasCompareAtPrice() {
      return this.compareAtPriceCents > 0 && this.compareAtPriceCents > this.basePriceCents;
    }

    getDiscountAmountPerUnitCents() {
      return Math.floor((this.basePriceCents * this.discountPercent) / 100);
    }

    getDiscountedPriceCents() {
      return this.basePriceCents - this.getDiscountAmountPerUnitCents();
    }

    getSavedCents(qty) {
      return this.getDiscountAmountPerUnitCents() * qty;
    }

    getTotalCents(qty) {
      return this.getDiscountedPriceCents() * qty;
    }

    isDealQty(qty) {
      return qty >= this.threshold;
    }

    apply() {
      if (this.hasCompareAtPrice() || this.isInBulkList()) {
        this.deactivate();
        return;
      }

      this.activate();
      this.qty = this.getCurrentQty();
      this.updateUI();
    }

    activate() {
      this.isActive = true;
      this.classList.remove('hidden');
      this.classList.add('is-active');
      this.notifyStickyBarUpdate();
      this.updatePageLayout(true);
    }

    deactivate() {
      this.isActive = false;
      this.classList.add('hidden');
      this.classList.remove('is-active');
      this.restoreDefaultButton();
      this.notifyStickyBarUpdate();
      this.updatePageLayout(false);
    }

    notifyStickyBarUpdate() {
      window.dispatchEvent(new CustomEvent('quantity-deal:observer-update'));
    }

    getProductInfoEl() {
      return document.getElementById(`MainProduct-${this.sectionId}`);
    }

    updatePageLayout(active) {
      const productInfo = this.getProductInfoEl();
      if (!productInfo) return;
      productInfo.classList.toggle('quantity-deal-page-active', active);
    }

    getSubmitButton() {
      return document.getElementById(`ProductSubmitButton-${this.sectionId}`);
    }

    getQuantityInput() {
      return this.form?.querySelector('input[name="quantity"]');
    }

    getCurrentQty() {
      const input = this.getQuantityInput();
      const parsed = parseInt(input?.value, 10);
      return parsed && parsed > 0 ? parsed : 1;
    }

    saveOriginalBtnText() {
      const btn = this.getSubmitButton();
      if (btn) {
        const textEl = btn.querySelector('.btn__text');
        this.originalBtnText = textEl?.innerHTML?.trim() || textEl?.textContent?.trim() || '';
      }
    }

    getDefaultButtonText() {
      return this.originalBtnText || FoxTheme?.variantStrings?.addToCart || 'Add to cart';
    }

    restoreDefaultButton() {
      const btn = this.getSubmitButton();
      const textEl = btn?.querySelector('.btn__text');
      if (textEl) textEl.innerHTML = this.getDefaultButtonText();
    }

    formatMoney(cents) {
      return FoxTheme.Currency.formatMoney(cents, FoxTheme.settings.moneyFormat);
    }

    formatMoneyPlain(cents) {
      const formatted = this.formatMoney(cents);
      const temp = document.createElement('span');
      temp.innerHTML = formatted;
      return temp.textContent || formatted;
    }

    formatDiscount(percent) {
      const template = this.i18n.discount || '−{{ percent }}%';
      return template.replace('{{ percent }}', String(Math.round(percent)));
    }

    pcLabel(count) {
      const template = count === 1 ? this.i18n.pc_one : this.i18n.pc_other;
      return (template || '{{ count }} pcs').replace('{{ count }}', String(count));
    }

    formatDealTitle(count) {
      const template = this.i18n.deal_title || '{{ count }} pcs · Save {{ percent }}%';
      return template
        .replace('{{ count }}', String(count))
        .replace('{{ percent }}', String(Math.round(this.discountPercent)));
    }

    formatTotal(amountCents) {
      const template = this.i18n.total || '{{ amount }} total';
      return template.replace('{{ amount }}', this.formatMoneyPlain(amountCents));
    }

    formatYouSaveLabel() {
      return (this.i18n.you_save || 'You save').replace('{{ amount }}', '').trim();
    }

    formatAddDealCta(count, totalCents) {
      const template = this.i18n.add_deal_to_cart || 'Add {{ count }} pcs to cart — {{ amount }}';
      return template
        .replace('{{ count }}', String(count))
        .replace('{{ amount }}', this.formatMoneyPlain(totalCents));
    }

    formatAddToCartCta(count, totalCents) {
      const template = this.i18n.add_to_cart || 'Add {{ count }} pcs to cart — {{ amount }}';
      return template
        .replace('{{ count }}', String(count))
        .replace('{{ amount }}', this.formatMoneyPlain(totalCents));
    }

    setQuantityInput(value) {
      const input = this.getQuantityInput();
      if (!input) return;
      input.value = value;
      input.dispatchEvent(new Event('change', { bubbles: true }));
      FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.quantityUpdate, undefined);
    }

    syncVariantMeta(variant) {
      if (!variant) return;
      this.variantSku = variant.sku || '';
      this.basePriceCents = variant.price || 0;
      this.compareAtPriceCents = variant.compare_at_price || 0;
      this.inventoryManagement = variant.inventory_management || '';
      this.inventoryQuantity = Number.isFinite(variant.inventory_quantity)
        ? variant.inventory_quantity
        : NaN;
    }

    getShopCurrencyUnitPrice() {
      const presentmentPrice = this.getDiscountedPriceCents() / 100;
      const rate = Number(window.Shopify?.currency?.rate || 1);
      if (!rate) return presentmentPrice;
      return presentmentPrice / rate;
    }

    trackLostOpportunity() {
      if (typeof window.staleksTrack !== 'function') return;
      if (!this.inventoryManagement || !Number.isFinite(this.inventoryQuantity)) return;

      const trackKey = [
        '01M0FBFS9NJZPB48QWJTPBCGE1',
        this.productId || '',
      ].join(':');
      if (trackedLostOpportunityKeys.has(trackKey)) return;

      const requestedQty = this.threshold;
      const stock = Math.max(this.inventoryQuantity, 0);
      const shortage = requestedQty - stock;
      if (shortage <= 0) return;

      trackedLostOpportunityKeys.add(trackKey);

      window.staleksTrack({
        id: '01M0FBFS9NJZPB48QWJTPBCGE1',
        value: Number((this.getShopCurrencyUnitPrice() * shortage).toFixed(2)),
        payload: {
          product_id: Number(this.productId) || this.productId,
          product_sku: this.variantSku || '',
        },
      });
    }

    addDealToCart() {
      if (!this.form) return;
      this.trackLostOpportunity();
      this.setQuantityInput(this.threshold);
      this.qty = this.threshold;
      this.updateUI();
      if (typeof this.form.requestSubmit === 'function') {
        this.form.requestSubmit();
      } else {
        this.form.submit();
      }
    }

    updateDealCard() {
      const dealQty = this.threshold;
      const discountedPerUnit = this.getDiscountedPriceCents();
      const dealTotal = this.getTotalCents(dealQty);
      const dealSaved = this.getSavedCents(dealQty);

      if (this.titleEl) {
        this.titleEl.textContent = this.formatDealTitle(dealQty);
      }
      if (this.priceEl) {
        this.priceEl.innerHTML = this.formatMoney(discountedPerUnit);
      }
      if (this.compareEl) {
        this.compareEl.innerHTML = this.formatMoney(this.basePriceCents);
      }
      if (this.dealBtn) {
        this.dealBtn.textContent = this.formatAddDealCta(dealQty, dealTotal);
      }

      const displayQty = this.isDealQty(this.qty) ? this.qty : dealQty;
      const displaySaved = this.getSavedCents(displayQty);
      const displayTotal = this.getTotalCents(displayQty);

      if (this.saveRight) {
        this.saveRight.classList.toggle('is-idle', !this.isDealQty(this.qty));
      }
      if (this.saveAmountEl) {
        this.saveAmountEl.textContent = this.formatMoneyPlain(displaySaved);
      }
      if (this.saveTotalEl) {
        this.saveTotalEl.textContent = this.formatTotal(displayTotal);
      }
    }

    updateMainButton() {
      if (!this.isDealQty(this.qty)) {
        this.restoreDefaultButton();
        return;
      }

      const totalCents = this.getTotalCents(this.qty);
      const btn = this.getSubmitButton();
      const textEl = btn?.querySelector('.btn__text');
      if (textEl) {
        textEl.textContent = this.formatAddToCartCta(this.qty, totalCents);
      }
    }

    updateUI() {
      if (!this.isActive) return;
      this.updateDealCard();
      this.updateMainButton();
      this.notifyStickyBarUpdate();
    }
  }

  customElements.define('quantity-deal-promo', QuantityDealPromo);
}
